import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useAuthContext } from "../../../context/AuthContext";
import organizationService from "../../../services/organization.service";
import {
  BasicTreeNodeDataType,
  ProEntitiesTree,
  TreeNode,
} from "../../../api/index.defs";
import styles from "./EntitySelectTreeView.module.css";
import Tree from "rc-tree";
import { DataNode, EventDataNode, Key } from "rc-tree/lib/interface";
import folderIcon from "../../../assets/tree/folder.svg";
import folderOpenIcon from "../../../assets/tree/folder_open.svg";
import expandIcon from "../../../assets/tree/expand_arrow.svg";

type OnSelectNodeFunc = {
  event: "select";
  selected: boolean;
  node: EventDataNode;
  selectedNodes: DataNode[];
  nativeEvent: MouseEvent;
};

type IconPropsType = { isExpanded: boolean };
const Icon = ({ isExpanded }: IconPropsType): JSX.Element => (
  <img
    className="cursor-pointer w-100 h-100"
    style={{ transform: "translateY(-25%)" }}
    src={isExpanded ? folderOpenIcon : folderIcon}
    alt=""
  />
);

type SwitcherIconPropsType = IconPropsType & { hasChildren: boolean };
const SwitcherIcon = ({ isExpanded, hasChildren }: SwitcherIconPropsType) =>
  hasChildren ? (
    <img
      className="cursor-pointer w-100 h-100"
      style={{
        transform: `translateY(-25%) rotate(${
          isExpanded ? 90 : 0
        }deg) scale(1.8)`,
      }}
      src={expandIcon}
      alt=""
    />
  ) : (
    <></>
  );

function mapToDataNode(obj: TreeNode<BasicTreeNodeDataType>) {
  const newObj: DataNode = { key: obj.data.id };
  if (obj.hasOwnProperty("data") && obj.hasOwnProperty("children")) {
    newObj.key = obj.data.id;
    newObj.title = obj.data.name;
    obj.children &&
      (newObj.children = obj.children.map((child) => mapToDataNode(child)));
  }
  return newObj;
}

type EntityTreePropsType = {
  entityTree: ProEntitiesTree | null;
  onSelectEntity: (keys: Key[], event: OnSelectNodeFunc) => void;
};
const EntityTree = ({ entityTree, onSelectEntity }: EntityTreePropsType) => {
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const handleExpand = (keys: Key[]): void => setExpandedKeys(keys);
  const noEntities = !entityTree || !entityTree.root;

  useEffect(() => {
    if (entityTree && entityTree.root) {
      handleExpand([entityTree.root.data.id]);
      //EXPAND ROOT ELEMENT
      const rootSwitcher = document.querySelector(".rc-tree-switcher");
      rootSwitcher &&
        rootSwitcher.dispatchEvent(new MouseEvent("click", { bubbles: true }));
    }
  }, [entityTree]);

  return (
    <div className={`${styles.dropdownContent} tree`}>
      {noEntities ? (
        " "
      ) : (
        <Tree
          onExpand={handleExpand}
          treeData={[mapToDataNode(entityTree!.root!)]}
          onSelect={onSelectEntity}
          switcherIcon={({ expanded, data }) => (
            <SwitcherIcon
              isExpanded={!!expanded}
              hasChildren={!!data?.children?.length}
            />
          )}
          icon={({ data }) => (
            <Icon isExpanded={expandedKeys.includes(data?.key as Key)} />
          )}
        />
      )}
    </div>
  );
};

const findNodeById = (
  id: number,
  node: TreeNode<BasicTreeNodeDataType>
): TreeNode<BasicTreeNodeDataType> | null => {
  if (node.data.id === id) return node;
  if (node.children && node.children.length > 0) {
    for (let child of node.children) {
      const foundNode = findNodeById(id, child);
      if (foundNode) return foundNode;
    }
  }
  return null;
};

type EntitySelectTreeViewProps = {
  disabled?: boolean;
  setValueOnChange?: (id: number) => void;
  selectedOrgId?: number;
  selectedUserId?: string;
};
const EntitySelectTreeView = ({
  disabled,
  setValueOnChange,
  selectedOrgId,
  selectedUserId,
}: EntitySelectTreeViewProps) => {
  const {
    state: { user },
  } = useAuthContext();

  const intl = useIntl();

  const entityPlaceholder = intl.messages[
    "MANAGEMENT.ADD_USER.ENTITY_REQUIRED"
  ] as string;
  const loadingPlaceholder = intl.messages["GLOBAL.LOADING"] as string;

  const [isTreeOpen, setIsTreeOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<{
    key: number;
    title: string;
  } | null>(null);

  const [entities, setEntities] = useState<ProEntitiesTree | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!user?.organization?.id) return;
    setIsLoading(true);
    organizationService
      .getProEntitiesTree(user.organization.id)
      .then((res) => {
        setEntities(res || null);
        setIsLoading(false);
      })
      .catch(() => {
        setEntities(null);
        setIsLoading(false);
      });
  }, [user?.organization?.id]);

  useEffect(() => {
    if (!entities || !selectedOrgId || !entities.root) return;
    const selectedEntity = findNodeById(selectedOrgId, entities.root);
    selectedEntity &&
      setSelectedEntity({
        key: selectedOrgId,
        title: selectedEntity.data.name,
      });
  }, [selectedUserId, selectedOrgId, entities]);

  useEffect(() => setIsTreeOpen(false), [selectedUserId]);

  const onSelectEntity = (keys: Key[], event: OnSelectNodeFunc) => {
    const { key, title } = event.node;
    setSelectedEntity({ key: +key, title: title!.toString() });
    setValueOnChange && setValueOnChange(+key);
    setIsTreeOpen(false);
  };

  const disableDropdown = disabled || isLoading;

  return (
    <div className={styles.dropdownContainer}>
      <div
        onClick={() => setIsTreeOpen(!isTreeOpen)}
        className={`${styles.dropdownInput} ${
          disableDropdown ? styles.dropdownInput__disabled : ""
        }`}
      >
        <div>
          {!selectedEntity
            ? isLoading
              ? loadingPlaceholder
              : entityPlaceholder
            : selectedEntity.title}
        </div>
        <div>
          <img className={styles.dropdownIcon} src={expandIcon} alt="" />
        </div>
      </div>
      {isTreeOpen ? (
        <EntityTree entityTree={entities} onSelectEntity={onSelectEntity} />
      ) : (
        ""
      )}
    </div>
  );
};
export default EntitySelectTreeView;
